// @ts-nocheck
// Places store, backed by SQLite WASM (OPFS SAHPool) — see ./localDb.js.
// Runs INSIDE the dedicated DB worker (./worker.js); the main thread uses the
// same-shaped proxy in #lib/locationDB.js. Public API is unchanged from the old
// IndexedDB version: initialize, add, addLocations, getAll, update, delete,
// deleteAll, getLocationsByTimeRange, normalizeLocation.

import { getDb, selectAll, jsonOrNull, parseJson } from './localDb.js';
import {
	assertAccountWritableInTransaction,
	enqueueMutationInTransaction,
	mergeKeepingExisting,
	placeToSyncData,
	validEntityId
} from './syncCore.js';
import { normalizeCustomMarkerData } from '../components/icons.js';

const COLUMNS = [
	'owner_uid', 'id', 'lat', 'lng', 'name', 'description', 'address', 'type',
	'selectedCategories', 'customMarkerData', 'pinned',
	'originalName', 'place_id', 'addressObject', 'attribution', 'place_name',
	'timestamp', 'extras',
];
const INSERT_SQL =
	`INSERT OR REPLACE INTO locations (${COLUMNS.join(', ')}) VALUES (${COLUMNS.map(() => '?').join(', ')})`;

function newId() {
	return typeof crypto !== 'undefined' && crypto.randomUUID
		? crypto.randomUUID()
		: 'p_' + Math.random().toString(36).slice(2);
}

function tsOf(p) {
	if (Number.isFinite(p.timestamp)) return p.timestamp;
	if (p.timestamp instanceof Date) return p.timestamp.getTime();
	return Date.now();
}

function validCoordinates(p) {
	return Number.isFinite(p?.lat) && p.lat >= -90 && p.lat <= 90 &&
		Number.isFinite(p?.lng) && p.lng >= -180 && p.lng <= 180;
}

// Map a place object → bind values in COLUMNS order. JSON columns go through
// JSON.stringify, which reads cleanly through Svelte $state proxies (so the
// DataCloneError that bit the IndexedDB layer can't happen here).
function locBind(p, ownerUid, id, ts) {
	const cats = Array.isArray(p.selectedCategories) && p.selectedCategories.length ? p.selectedCategories : null;
	return [
		ownerUid,
		id,
		p.lat,
		p.lng,
		p.name ?? null,
		p.description ?? null,
		p.address ?? null,
		Number.isFinite(p.type) ? p.type : null,
		jsonOrNull(cats),
		jsonOrNull(normalizeCustomMarkerData(p.customMarkerData)),
		p.pinned ? 1 : 0,
		p.originalName ?? null,
		p.place_id ?? null,
		jsonOrNull(p.addressObject),
		p.attribution ?? null,
		p.place_name ?? null,
		ts,
		jsonOrNull(p.extras),
	];
}

function rawRows(db, sql, bind = []) {
	const result = [];
	db.exec({ sql, bind, rowMode: 'object', resultRows: result });
	return result;
}

function rowToLocation(r) {
	const out = { id: r.id, lat: r.lat, lng: r.lng };
	if (r.name != null) out.name = r.name;
	if (r.description != null) out.description = r.description;
	if (r.address != null) out.address = r.address;
	if (r.type != null) out.type = r.type;
	const cats = parseJson(r.selectedCategories);
	if (cats) out.selectedCategories = cats;
	const cmd = parseJson(r.customMarkerData);
	const marker = normalizeCustomMarkerData(cmd);
	if (marker) out.customMarkerData = marker;
	if (r.pinned) out.pinned = true;
	if (r.originalName != null) out.originalName = r.originalName;
	if (r.place_id != null) out.place_id = r.place_id;
	const ao = parseJson(r.addressObject);
	if (ao) out.addressObject = ao;
	if (r.attribution != null) out.attribution = r.attribution;
	if (r.place_name != null) out.place_name = r.place_name;
	if (r.timestamp != null) out.timestamp = r.timestamp;
	const ex = parseJson(r.extras);
	if (ex) out.extras = ex;
	return out;
}

export { mergeKeepingExisting };

export class LocationsDataService {
	constructor(ownerUid = '') {
		this.ownerUid = typeof ownerUid === 'string' ? ownerUid : '';
	}

	setOwnerUid(ownerUid) {
		this.ownerUid = typeof ownerUid === 'string' ? ownerUid : '';
	}

	async initialize() {
		await getDb();
		return true;
	}

	// Upsert keyed by coordinates: a place saved again at the same spot updates
	// the stored place instead of adding a second pin there.
	async add(location) {
		if (!validCoordinates(location)) {
			throw new Error('valid lat/lng required');
		}
		const ownerUid = this.ownerUid;
		const raw = await getDb();
		let id;
		raw.transaction(() => {
			id = addLocationInTransaction(raw, ownerUid, location);
		});
		return id;
	}

	// Bulk add (file import, "Save copy"). Never replaces a saved place: when a
	// place already exists at the same coordinates the stored one is kept and the
	// incoming one only fills its empty fields. Returns the ids in input order.
	async addLocations(locations) {
		return (await this.importLocations(locations)).ids;
	}

	// Same as addLocations, with a report of what happened:
	// { ids, added, updated, unchanged }.
	async importLocations(locations) {
		if (!Array.isArray(locations) || locations.length === 0) {
			throw new Error('Invalid locations data');
		}
		const ownerUid = this.ownerUid;
		const db = await getDb();
		const normalized = locations.map((l) => this.normalizeLocation(l));
		if (normalized.some((place) => !validCoordinates(place))) throw new Error('valid lat/lng required');
		let result;
		db.transaction(() => {
			result = importLocationsInTransaction(db, ownerUid, normalized);
		});
		return result;
	}

	async getAll() {
		const ownerUid = this.ownerUid;
		const rows = await selectAll('SELECT * FROM locations WHERE owner_uid = ?', [ownerUid]);
		return rows.map(rowToLocation);
	}

	async getAllForOwner(ownerUid) {
		const rows = await selectAll('SELECT * FROM locations WHERE owner_uid = ?', [ownerUid || '']);
		return rows.map(rowToLocation);
	}

	async update(locationData) {
		if (!locationData?.id) throw new Error('Location ID is required for update');
		const ownerUid = this.ownerUid;
		const raw = await getDb();
		let merged;
		raw.transaction(() => {
			assertAccountWritableInTransaction(raw, ownerUid);
			const existing = rawRows(raw,
				'SELECT * FROM locations WHERE owner_uid = ? AND id = ?',
				[ownerUid, locationData.id])[0];
			if (!existing) throw new Error('Location not found');
			// Merge over the stored row; preserve the original timestamp (an edit is not
			// a re-creation).
			merged = { ...rowToLocation(existing), ...locationData };
			raw.exec({ sql: INSERT_SQL, bind: locBind(merged, ownerUid, merged.id, tsOf(merged)) });
			const data = placeToSyncData(merged);
			if (data) enqueueMutationInTransaction(raw, {
				ownerUid, kind: 'place', id: merged.id, op: 'upsert', data
			});
		});
		return merged.id;
	}

	async delete(id) {
		const ownerUid = this.ownerUid;
		const raw = await getDb();
		raw.transaction(() => {
			assertAccountWritableInTransaction(raw, ownerUid);
			const existing = rawRows(raw,
				'SELECT 1 AS found FROM locations WHERE owner_uid = ? AND id = ?',
				[ownerUid, id])[0];
			if (!existing) return;
			raw.exec({ sql: 'DELETE FROM locations WHERE owner_uid = ? AND id = ?', bind: [ownerUid, id] });
			enqueueMutationInTransaction(raw, {
				ownerUid, kind: 'place', id, op: 'delete'
			});
		});
	}

	async deleteAll() {
		const ownerUid = this.ownerUid;
		const raw = await getDb();
		raw.transaction(() => {
			assertAccountWritableInTransaction(raw, ownerUid);
			const existing = rawRows(raw, 'SELECT id FROM locations WHERE owner_uid = ?', [ownerUid]);
			raw.exec({ sql: 'DELETE FROM locations WHERE owner_uid = ?', bind: [ownerUid] });
			for (const item of existing) enqueueMutationInTransaction(raw, {
				ownerUid, kind: 'place', id: item.id, op: 'delete'
			});
		});
	}

	async getLocationsByTimeRange(start, end) {
		const ownerUid = this.ownerUid;
		const s = start instanceof Date ? start.getTime() : Number(start);
		const e = end instanceof Date ? end.getTime() : Number(end);
		const rows = await selectAll(
			'SELECT * FROM locations WHERE owner_uid = ? AND timestamp BETWEEN ? AND ? ORDER BY timestamp',
			[ownerUid, s, e]
		);
		return rows.map(rowToLocation);
	}

	normalizeLocation(location) {
		return normalizeLocation(location);
	}
}

// Canonical place shape used by the import path. Mirrors the union of fields
// across web/Android/import parsers; only persists a custom marker when one was
// actually chosen, and carries the pinned flag + Android metadata through.
// Pure — exported standalone so the main-thread proxy can run it without an RPC.
export function normalizeLocation(location) {
	const timestamp = location.timestamp instanceof Date ? location.timestamp.getTime() : location.timestamp;
	const extras = location.extras && typeof location.extras === 'object' && !Array.isArray(location.extras) &&
		Object.keys(location.extras).length ? location.extras : null;
	return {
		...(validEntityId(location.id) ? { id: location.id } : {}),
		lat: location.lat,
		lng: location.lng,
		type: location.type,
		name: location.name,
		description: location.description,
		address: location.address,
		selectedCategories: location.selectedCategories || [],
		...(normalizeCustomMarkerData(location.customMarkerData)
			? { customMarkerData: normalizeCustomMarkerData(location.customMarkerData) }
			: {}),
		...(location.pinned ? { pinned: true } : {}),
		...(location.originalName && { originalName: location.originalName }),
		...(location.place_id && { place_id: location.place_id }),
		...(location.addressObject && { addressObject: location.addressObject }),
		...(location.attribution && { attribution: location.attribution }),
		...(location.place_name && { place_name: location.place_name }),
		...(Number.isFinite(timestamp) && timestamp > 0 ? { timestamp } : {}),
		...(extras ? { extras } : {}),
	};
}

/**
 * A place saved on top of a stored one: what was entered now wins, what was
 * left empty now keeps its stored value, and categories are joined. Pure.
 */
export function overlayEntered(existing, entered) {
	// mergeKeepingExisting fills the blanks of its first argument from the second.
	const { place } = mergeKeepingExisting(entered, existing);
	const stored = Array.isArray(existing.selectedCategories) ? existing.selectedCategories.filter(Boolean) : [];
	const added = (Array.isArray(entered.selectedCategories) ? entered.selectedCategories : [])
		.filter((c) => typeof c === 'string' && c.trim() && !stored.includes(c));
	place.selectedCategories = [...stored, ...new Set(added)];
	return place;
}

/**
 * The write half of add(). When a place is already stored at that spot, what
 * the user has just entered wins, and everything else (the pin to top, the
 * note, categories, the creation time, Android metadata) is kept.
 */
export function addLocationInTransaction(db, ownerUid, location) {
	assertAccountWritableInTransaction(db, ownerUid);
	const existing = rawRows(db,
		'SELECT * FROM locations WHERE owner_uid = ? AND lat = ? AND lng = ?',
		[ownerUid, location.lat, location.lng])[0];
	const candidate = existing?.id || location.id;
	const id = validEntityId(candidate) ? candidate : newId();
	const place = existing ? { ...overlayEntered(rowToLocation(existing), location), id } : { ...location, id };
	const timestamp = existing?.timestamp ?? tsOf(location);
	db.exec({ sql: INSERT_SQL, bind: locBind(place, ownerUid, id, timestamp) });
	const data = placeToSyncData({ ...place, id, timestamp });
	if (data) enqueueMutationInTransaction(db, {
		ownerUid, kind: 'place', id, op: 'upsert', data
	});
	return id;
}

/**
 * The write half of importLocations. Runs inside the caller's transaction so a
 * failure rolls the whole import back. `db` is the SQLite handle (or a test
 * double with the same exec() contract).
 */
export function importLocationsInTransaction(db, ownerUid, places) {
	assertAccountWritableInTransaction(db, ownerUid);
	const result = { ids: [], added: 0, updated: 0, unchanged: 0 };
	for (const p of places) {
		const found = rawRows(db,
			'SELECT * FROM locations WHERE owner_uid = ? AND lat = ? AND lng = ?',
			[ownerUid, p.lat, p.lng])[0];
		if (found) {
			const { place, changed } = mergeKeepingExisting(rowToLocation(found), p);
			result.ids.push(found.id);
			if (!changed) {
				result.unchanged++;
				continue;
			}
			// Keep the stored timestamp exactly as it is (even when it is empty).
			db.exec({ sql: INSERT_SQL, bind: locBind(place, ownerUid, found.id, found.timestamp ?? null) });
			const data = placeToSyncData({ ...place, id: found.id });
			if (data) enqueueMutationInTransaction(db, { ownerUid, kind: 'place', id: found.id, op: 'upsert', data });
			result.updated++;
			continue;
		}
		let id = validEntityId(p.id) ? p.id : newId();
		// An id that is already taken by a different place must not be replaced.
		if (rawRows(db, 'SELECT 1 AS found FROM locations WHERE owner_uid = ? AND id = ?', [ownerUid, id])[0]) id = newId();
		const timestamp = tsOf(p);
		db.exec({ sql: INSERT_SQL, bind: locBind(p, ownerUid, id, timestamp) });
		const data = placeToSyncData({ ...p, id, timestamp });
		if (data) enqueueMutationInTransaction(db, { ownerUid, kind: 'place', id, op: 'upsert', data });
		result.ids.push(id);
		result.added++;
	}
	return result;
}
